import { ChangeEvent, useEffect, useState, useRef } from "react";
import { useAppContext } from "contexts/AppContext";
import { BUILDER_CONFIG, CONFIG_CUSTOMIZATION, CUSTOMIZATION_THEME_SETTINGS, THEME_PLATFORMS, THEME_SECONDARY } from "constants/constants";
import { TInfoText } from "types";
import Title from "components/Header/Title";
import { SlMenu } from "react-icons/sl";

interface RangeStyles extends React.CSSProperties {
  left: string;
  width: string;
}

interface RangeSliderProps {
  id: string;
  name: string;
  onChange: (value: [number, number], disabled: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  min?: number;
  max?: number;
  step?: number;
  description?: string;
  info?: TInfoText;
  showLabel?: boolean;
  isFilter?: boolean;
  initialMin?: number;
  initialMax?: number;
  currentMin?: number;
  currentMax?: number;
  showValues?: boolean;
  value: [number, number] | undefined;
  customThumb?: React.ReactNode;
  thumbSize?: number;
}

const RangeSlider = ({
  id,
  name,
  value,
  onChange,
  disabled = false,
  required = false,
  step = 1,
  description,
  info,
  showLabel = true,
  isFilter = false,
  initialMin,
  initialMax,
  showValues = true,
  thumbSize = 18,
  currentMin,
  currentMax,
}: RangeSliderProps) => {
  const { platform, builderSpec } = useAppContext();
  const [isResetting, setIsResetting] = useState(false);

  const min = initialMin ?? 0
  const max = initialMax ?? 1
  
  // Set initial values either from props or defaults
  const [currentMinValue, setCurrentMinValue] = useState<number>(
    value?.[0] !== undefined ?  value[0] : (initialMin !== undefined ? initialMin : min)
  );
  // For the max value, ensure it's at least the min value
  // This is especially important when min=0, max=1, step=1
  const defaultMax = (initialMax !== undefined ? initialMax : max);
  const safeInitialMax = value?.[1] !== undefined ? value[1] : 
                        (defaultMax > currentMinValue ? defaultMax : 
                         Math.min(currentMinValue + step, max));
  
  const [currentMaxValue, setCurrentMaxValue] = useState<number>(safeInitialMax);

  // const [error, setError] = useState<string | null>(null);

  // Update local state when props change
  useEffect(() => {
      setCurrentMinValue(value?.[0] ?? currentMin ?? initialMin ?? 0);
      setCurrentMaxValue(value?.[1] ?? currentMax ?? initialMax ?? 0);
  }, [value, currentMax, currentMin]);

  // Calculate percentage for styles
  const getPercent = (value: number): number => {
    return Math.round(((value - min) / (max - min)) * 100);
  };

  // Calculate positions for custom thumbs
  const minThumbPosition = getPercent(currentMinValue);
  const maxThumbPosition = getPercent(currentMaxValue);

  // Custom thumb JSX or default
  const thumbElement = (
    <div className="sb3-aspect-square sb3-p-1 sb3-bg-white sb3-rounded-full sb3-z-30  sb3-border sb3-border-[#6D788C] sb3-shadow-md">
      <SlMenu className="sb3-rotate-90 sb3-text-[9px]"/>
    </div>
  );

  // Update range when values change
  useEffect(() => {
    // Calculate the left and right percentages
    const minPercent = (currentMin ? getPercent(currentMin) : initialMin && getPercent(initialMin)) ?? 0;
    const maxPercent = (currentMax ? getPercent(currentMax) : initialMax && getPercent(initialMax)) ?? 0;
    
    // Apply custom styles to represent the selected range
    const rangeElement = document.querySelector(`.range-selected-${id}`) as HTMLElement;
    if (rangeElement) {
      const styles: RangeStyles = {
        left: `${minPercent}%`,
        width: `${maxPercent - minPercent}%`
      };
      
      Object.assign(rangeElement.style, styles);
    }
  }, [currentMin, currentMax, min, max, id]);

  // Notify parent component when values change
  const handleValuesChange = () => {
    if (onChange) {
      const isDisabled = currentMinValue > (currentMax ?? 1) || currentMaxValue < (currentMin ?? 0)

      if(isDisabled) {
        // Set resetting state to true to enable transitions
        setIsResetting(true);
        
        // Reset values after a brief delay
        setTimeout(() => {
          setCurrentMinValue(value?.[0] ?? currentMin ?? initialMin ?? 0);
          setCurrentMaxValue(value?.[1] ?? currentMax ?? initialMax ?? 0);
          
          // Turn off transitions after reset animation completes
          setTimeout(() => {
            setIsResetting(false);
          }, 300);
        }, 10);
      }
      onChange([currentMinValue, currentMaxValue], isDisabled);
    }
  };

  // Handle min thumb change
  const handleMinChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(e.target.value);
    // When max = min + step, we need special handling
    if (max - min === step) {
      // For binary choices (like 0/1), allow min to be equal to max
      setCurrentMinValue(newValue);
    } else {
      // Normal case: ensure min < max
      if (newValue < currentMaxValue) {
        setCurrentMinValue(newValue);
      } else {
        // Ensure we don't go below the minimum allowed value
        const nextValue = Math.max(parseFloat((currentMaxValue - step).toFixed(10)), min);
        setCurrentMinValue(nextValue);
      }
    }
  };

  // Handle max thumb change
  const handleMaxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(e.target.value);
    // When max = min + step, we need special handling
    if (max - min === step) {
      // For binary choices (like 0/1), allow max to be equal to min
      setCurrentMaxValue(newValue);
    } else {
      // Normal case: ensure max > min
      if (newValue > currentMinValue) {
        setCurrentMaxValue(newValue);
      } else {
        // Ensure we don't go beyond the maximum allowed value
        const nextValue = Math.min(parseFloat((currentMinValue + step).toFixed(10)), max);
        setCurrentMaxValue(nextValue);
      }
    }
  };

  // Handle blur events for both sliders
  const handleBlur = () => {
    handleValuesChange();
  };
  
  return (
    <div className="sb3-range-slider-container">
      <Title title={name} required={required} description={description} info={info} showLabel={showLabel} id={id}/>
      
      <div className={`sb3-space-y-3 sb3-mt-6 ${isFilter ? 'sb3-w-full' : 'sb3-w-60'}`}>
        <div className=" sb3-range-slider-track-container sb3-relative sb3-h-[3px]">
          {/* Range track */}
          <div className="sb3-range-slider-track sb3-z-10 !sb3-left-0 sb3-absolute !sb3-w-full sb3-h-[3px] sb3-bg-[#DADBDD] sb3-rounded "></div>
          
          {/* Current range */}
          <div className={`range-selected-${id} sb3-z-20 sb3-pointer-events-none sb3-range-slider-selected sb3-absolute sb3-h-[3px] sb3-bg-[#6D788C] sb3-rounded 
            ${isResetting ? 'sb3-transition-all sb3-duration-300 sb3-ease-in-out' : ''}
          `}></div>
          
          {/* Min thumb */}
          <input
            type="range"
            id={`${id}-min`}
            min={min}
            max={max}
            value={currentMinValue}
            step={step}
            disabled={disabled}
            onChange={handleMinChange}
            onMouseUp={handleBlur}            
            onTouchEnd={handleBlur}
            style={{ 
              backgroundColor: platform === THEME_PLATFORMS.BIG_COMMERCE && !disabled 
                ? builderSpec?.[BUILDER_CONFIG]?.[CONFIG_CUSTOMIZATION]?.[CUSTOMIZATION_THEME_SETTINGS]?.[THEME_SECONDARY] 
                : undefined
            }}
            className={`
              sb3-range-slider-thumb sb3-range-slider-thumb-min 
              sb3-absolute sb3-w-full sb3-h-[3px] sb3-appearance-none sb3-pointer-events-none sb3-bg-transparent sb3-outline-none 
              sb3-z-10
              disabled:sb3-opacity-50 disabled:sb3-cursor-not-allowed
              [&::-webkit-slider-thumb]:sb3-appearance-none [&::-webkit-slider-thumb]:sb3-bg-transparent [&::-webkit-slider-thumb]:sb3-h-[25px] 
              [&::-webkit-slider-thumb]:sb3-w-[25px] [&::-webkit-slider-thumb]:sb3-rounded-full [&::-webkit-slider-thumb]:sb3-border-2 
              [&::-webkit-slider-thumb]:sb3-border-white [&::-webkit-slider-thumb]:sb3-pointer-events-auto [&::-webkit-slider-thumb]:sb3-cursor-pointer 
              [&::-moz-range-thumb]:sb3-appearance-none [&::-moz-range-thumb]:sb3-bg-blue-500 [&::-moz-range-thumb]:sb3-shadow-md [&::-moz-range-thumb]:sb3-h-[25px] 
              [&::-moz-range-thumb]:sb3-w-[25px] [&::-moz-range-thumb]:sb3-rounded-full [&::-moz-range-thumb]:sb3-border-2 
              [&::-moz-range-thumb]:sb3-border-white [&::-moz-range-thumb]:sb3-pointer-events-auto [&::-moz-range-thumb]:sb3-cursor-pointer
              [&::-moz-range-thumb]:-sb3-ml-2 [&::-webkit-slider-thumb]:-sb3-ml-2
            `}
          />
          
          {/* Max thumb */}
          <input
            type="range"
            id={`${id}-max`}
            min={min}
            max={max}
            value={currentMaxValue}
            step={step}
            disabled={disabled}
            onChange={handleMaxChange}
            onMouseUp={handleBlur}            
            onTouchEnd={handleBlur}
            style={{ 
              backgroundColor: platform === THEME_PLATFORMS.BIG_COMMERCE && !disabled 
                ? builderSpec?.[BUILDER_CONFIG]?.[CONFIG_CUSTOMIZATION]?.[CUSTOMIZATION_THEME_SETTINGS]?.[THEME_SECONDARY] 
                : undefined
            }}
            className={`
              sb3-range-slider-thumb sb3-range-slider-thumb-max 
              sb3-absolute sb3-w-full sb3-h-[3px] sb3-appearance-none sb3-pointer-events-none sb3-bg-transparent sb3-outline-none 
              sb3-z-10
              disabled:sb3-opacity-50 disabled:sb3-cursor-not-allowed
              [&::-webkit-slider-thumb]:sb3-appearance-none [&::-webkit-slider-thumb]:sb3-bg-transparent [&::-webkit-slider-thumb]:sb3-h-[25px] 
              [&::-webkit-slider-thumb]:sb3-w-[25px] [&::-webkit-slider-thumb]:sb3-rounded-full [&::-webkit-slider-thumb]:sb3-border-2 
              [&::-webkit-slider-thumb]:sb3-border-white [&::-webkit-slider-thumb]:sb3-pointer-events-auto [&::-webkit-slider-thumb]:sb3-cursor-pointer 
              [&::-moz-range-thumb]:sb3-appearance-none [&::-moz-range-thumb]:sb3-bg-blue-500 [&::-moz-range-thumb]:sb3-h-[25px] 
              [&::-moz-range-thumb]:sb3-w-[25px] [&::-moz-range-thumb]:sb3-rounded-full [&::-moz-range-thumb]:sb3-border-2 
              [&::-moz-range-thumb]:sb3-border-white [&::-moz-range-thumb]:sb3-pointer-events-auto [&::-moz-range-thumb]:sb3-cursor-pointer
              [&::-moz-range-thumb]:sb3-shadow-md [&::-moz-range-thumb]:-sb3-mr-2 [&::-webkit-slider-thumb]:-sb3-mr-2
            `}
          />
          
          {/* Custom thumbs (only rendered when customThumb prop is provided) */}
          {/* Custom min thumb */}
          <div 
            className={`sb3-range-slider-custom-thumb sb3-range-slider-custom-thumb-min sb3-absolute sb3-z-30 sb3-transform sb3-pointer-events-none
              ${isResetting ? 'sb3-transition-all sb3-duration-300 sb3-ease-in-out' : ''}
            `}
            style={{
              left: `calc(${minThumbPosition}% - ${thumbSize*minThumbPosition/100}px - ${(thumbSize/2)*minThumbPosition/100}px)`,
              top: '50%',
              transform: "translateY(-50%)",
            }}
          >
            {currentMinValue !== initialMin && <span className="sb3-absolute -sb3-top-full  sb3-left-1/2 -sb3-translate-x-1/2 sb3-text-xs">{currentMinValue}</span>}
            {thumbElement}
          </div>
          
          {/* Custom max thumb */}
          <div 
            className={`sb3-range-slider-custom-thumb sb3-range-slider-custom-thumb-max sb3-absolute sb3-z-30 sb3-transform sb3-pointer-events-none
              ${isResetting ? 'sb3-transition-all sb3-duration-300 sb3-ease-in-out' : ''}
            `}
            style={{
              left: `calc(${maxThumbPosition}% - ${thumbSize*maxThumbPosition/100}px + ${(thumbSize/2)*(100-maxThumbPosition)/100}px)`,
              top: '50%',
              transform: "translateY(-50%)",
            }}
          >
            {currentMaxValue !== initialMax && <span className="sb3-absolute -sb3-top-full  sb3-left-1/2 -sb3-translate-x-1/2 sb3-text-xs">{currentMaxValue}</span>}
            {thumbElement}
          </div>
        </div>
        
        {/* Display values */}
        {showValues && (
          <div className=" sb3-range-slider-values sb3-flex sb3-justify-between sb3-mt-2">
            <div className=" sb3-range-slider-min-value sb3-font-semibold sb3-text-[#6D788C] sb3-px-2 sb3-rounded sb3-text-xs">
              {initialMin}
            </div>
            <div className="sb3-range-slider-max-value sb3-font-semibold sb3-text-[#6D788C] sb3-px-2 sb3-rounded sb3-text-xs ">
              {initialMax}
            </div>
          </div>
        )}
        
        {/* {error && (
          <p id={`${id}-error`} className="sb3-text-sm sb3-text-red-500 sb3-range-slider-error">{error}</p>
        )} */}
      </div>
    </div>
  );
};

export default RangeSlider;